/**
 * CampaignCard - Card component for individual campaign items on the Kanban board
 */

import React from 'react';
import { Badge } from '../../components/ui/badge';
import { 
  GripVertical, Trash2, MoreVertical, FileText
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';

// Stage dot colors
const STAGE_DOT_COLORS = {
  decision: 'bg-emerald-500',
  consideration: 'bg-blue-500',
  retargeting: 'bg-orange-500',
  awareness: 'bg-violet-500',
  retention: 'bg-teal-500'
};

// Channel icons
const CHANNEL_ICONS = {
  search_paid: '🔍',
  meta_paid: '📱',
  linkedin_ads: '💼',
  retargeting: '🔄',
  email_lifecycle: '📧',
  partnerships: '🤝'
};

export default function CampaignCard({ 
  campaign, 
  onClick, 
  onDelete,
  isDragging = false,
  dragHandleProps = {}
}) {
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-700 font-semibold';
    if (score >= 60) return 'text-slate-700';
    return 'text-slate-500';
  };

  const stageDot = STAGE_DOT_COLORS[campaign.stage] || 'bg-slate-400';
  const channelIcon = CHANNEL_ICONS[campaign.channel] || '📢';

  return (
    <div
      className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-all cursor-pointer ${
        isDragging ? 'shadow-lg ring-2 ring-purple-400 opacity-90' : 'border-slate-200'
      }`}
      onClick={onClick}
      data-testid={`campaign-card-${campaign.bucket_campaign_id || campaign.confirmed_campaign_id}`}
    >
      {/* Card Content */}
      <div className="p-3">
        <div className="flex items-start gap-2">
          {/* Drag Handle */}
          <div 
            {...dragHandleProps}
            className="mt-1 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-4 h-4" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Campaign Name */}
            <h4 className="font-medium text-sm text-slate-800 line-clamp-2 mb-1">
              {campaign.name}
            </h4>
            
            {/* Plan Name */}
            <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
              <FileText className="w-3 h-3" />
              <span className="truncate">{campaign.plan_name}</span>
            </div>
            
            {/* Pills Row */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Stage with dot */}
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                <span className={`w-1.5 h-1.5 rounded-full ${stageDot}`}></span>
                {campaign.stage}
              </span>
              
              {/* Channel */}
              <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                {channelIcon} {campaign.channel?.replace('_', ' ').split(' ')[0]}
              </span>
            </div>
          </div>
          
          {/* Right side: Score + Menu */}
          <div className="flex items-start gap-1 shrink-0">
            <span className={`text-sm ${getScoreColor(campaign.recommendation_score || campaign.score)}`}>
              {campaign.recommendation_score || campaign.score}
            </span>
            
            {/* Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <MoreVertical className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  className="text-red-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
